// fieldCatalog.js
//
// Typed metadata for every field that's segmentable in user_profiles_marketing.
// Single source of truth — drives the field picker dropdown, the operator picker
// (operators are filtered by field type), and the value-input rendering in the
// segment editor.
//
// IMPORTANT: every `key` here must match an actual field on the
// user_profiles_marketing/{uid} doc. If you add a field, also update
// functions/helpers.js → buildUserMarketingProfile. See MARKETING_CAMPAIGNS_PLAN.md §3.1.

// --- Operator catalog (typed) ---
// Each entry: { value, label, valueArity, description, example }
export const OPERATORS = {
  eq:           {
    label: 'is', valueArity: 'one',
    description: 'Matches customers whose value is exactly the one you pick.',
    example: 'Emirate is Dubai — reaches customers whose main location is Dubai.',
  },
  neq:          {
    label: 'is not', valueArity: 'one',
    description: 'Excludes customers with this exact value.',
    example: 'Lifecycle stage is not churned — excludes customers who lapsed long ago.',
  },
  in:           {
    label: 'is one of', valueArity: 'list',
    description: 'Matches if the value is any one of your picks.',
    example: 'Emirate is one of Dubai or Sharjah — reaches customers in either emirate.',
  },
  notIn:        {
    label: 'is not one of', valueArity: 'list',
    description: 'Excludes customers matching any of your picks.',
    example: 'Lifecycle stage is not one of new or churned — excludes fresh signups and lapsed customers.',
  },
  gt:           {
    label: 'is more than', valueArity: 'one',
    description: 'Strictly greater. The number you enter is not included.',
    example: 'Lifetime value is more than 1000 AED — customers who spent more than 1000, not exactly 1000.',
  },
  gte:          {
    label: 'is at least', valueArity: 'one',
    description: 'Greater than or equal. The number you enter is included.',
    example: 'Total bookings is at least 5 — customers with 5 or more bookings.',
  },
  lt:           {
    label: 'is less than', valueArity: 'one',
    description: 'Strictly less. The number you enter is not included.',
    example: 'Days since last booking is less than 7 — booked in the last 6 days.',
  },
  lte:          {
    label: 'is at most', valueArity: 'one',
    description: 'Less than or equal. The number you enter is included.',
    example: 'Wallet expires in days is at most 3 — credit expires in the next 3 days.',
  },
  between:      {
    label: 'is between', valueArity: 'two',
    description: 'Between two numbers, both included. Enter the smaller number first.',
    example: 'Lifetime value between 500 and 1000 — customers who spent 500 to 1000 AED.',
  },
  contains:     {
    label: 'includes', valueArity: 'one',
    description: 'For list fields — matches customers whose list includes this value.',
    example: 'Vehicle types includes SUV — customers who own at least one SUV.',
  },
  containsAny:  {
    label: 'includes any of', valueArity: 'list',
    description: 'For list fields — matches if the list includes at least one of your picks.',
    example: 'Any saved emirate includes any of Dubai or Sharjah — customers with a place in either.',
  },
  notContains:  {
    label: 'does not include', valueArity: 'one',
    description: 'For list fields — excludes customers whose list contains this value.',
    example: 'Vehicle types does not include 2 wheeler — excludes motorbike owners.',
  },
  exists:       {
    label: 'is set', valueArity: 'none',
    description: 'The field has any value at all (not empty).',
    example: 'Checkout started is set — customers with an open cart right now.',
  },
  notExists:    {
    label: 'is empty', valueArity: 'none',
    description: 'The field has no value.',
    example: 'First booking date is empty — customers who never booked.',
  },
  withinRadius: {
    label: 'is within radius', valueArity: 'one', // value = { lat, lng, km }
    description: 'Location is within N km of a point you pick on the map.',
    example: 'Location within 5 km of Dubai Marina — customers whose place is close to that pin.',
  },
};

// Default operator sets per field-type (UI filters the dropdown by this)
// - `array` = string/enum arrays (allEmirates, allCities, tags, vehicleTypes)
// - `geo` = single GeoPoint (backwards-compat primary)
// - `geoList` = array of {latitude, longitude} points (allGeoPoints)
const OPS_BY_TYPE = {
  number:  ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'notExists'],
  string:  ['eq', 'neq', 'in', 'notIn', 'contains', 'notContains', 'exists', 'notExists'],
  enum:    ['eq', 'neq', 'in', 'notIn'],
  boolean: ['eq'],
  date:    ['gt', 'gte', 'lt', 'lte', 'between', 'exists', 'notExists'], // stored as ms-number
  array:   ['contains', 'containsAny', 'notContains', 'exists', 'notExists'],
  geo:     ['withinRadius'],
  geoList: ['withinRadius', 'exists', 'notExists'],
};

// --- Field catalog ---
// Grouped by category. Order matters for UI rendering.
// `path` is the dot-path on the user_profiles_marketing doc.
// `enumValues` for type='enum' drives the value-picker.
// `unit` is a display hint only.
export const FIELD_CATALOG = [
  // ---------- Demographics ----------
  // Location fields come in two flavours:
  //   • `city` / `emirate` / `geoPoint` — the user's PRIMARY place only (backwards compatible).
  //   • `allCities` / `allEmirates` / `allGeoPoints` — EVERY saved place. Use these when
  //     you want to reach users who have a home in Dubai AND an office in Sharjah, etc.
  { key: 'language',     path: 'language',     category: 'Demographics', type: 'enum',    label: 'App language',         enumValues: ['en', 'ar'],
    description: 'The language the customer uses in the app.',
    example: 'App language is ar — send Arabic copy to Arabic-speaking customers.',
    tips: ['Populates once customers open the next mobile app update. Older app versions do not report language.'] },
  // 'city' and 'allCities' hidden — city-level data has thousands of unique
  // Arabic + English variants and is unworkable as a segment field. Emirate
  // is the primary location dimension. Schema, profile builder, and resolver
  // still populate/support them for future re-enable.
  { key: 'emirate',      path: 'emirate',      category: 'Demographics', type: 'enum',    label: 'Emirate (main location)', enumValues: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al-Khaimah', 'Umm Al-Quwain', 'Fujairah'],
    description: 'The emirate of the customer\'s main saved location.',
    example: 'Emirate is Dubai — customers whose main location is somewhere in Dubai.' },
  { key: 'allEmirates',  path: 'allEmirates',  category: 'Demographics', type: 'array',   label: 'Any saved emirate',       enumValues: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al-Khaimah', 'Umm Al-Quwain', 'Fujairah'],
    description: 'Every emirate the customer has ever saved a place in.',
    example: 'Any saved emirate includes any of Dubai or Sharjah — customers with a place in either.' },
  { key: 'placeCount',   path: 'placeCount',   category: 'Demographics', type: 'number',  label: 'Number of saved locations',
    description: 'How many places the customer has saved (home, office, second home, etc.).',
    example: 'Number of saved locations is at least 2 — customers who wash at multiple places, usually high-intent.' },
  { key: 'platform',     path: 'platform',     category: 'Demographics', type: 'enum',    label: 'Device (iOS / Android)',         enumValues: ['ios', 'android'],
    description: 'The type of phone the customer uses.',
    example: 'Device is ios — reach iPhone users for an iOS-specific offer.',
    tips: ['Populates once customers open the next mobile app update.'] },
  { key: 'appVersion',   path: 'appVersion',   category: 'Demographics', type: 'string',  label: 'App version',
    description: 'The mobile app version the customer last opened.',
    example: 'App version is 3.4.1 — target a specific build, useful for feature launches.',
    tips: ['Populates once customers open the next mobile app update.'] },
  // 'geoPoint' and 'allGeoPoints' hidden from the picker — the current
  // withinRadius UX is a bare lat/lng/km input which is unusable without a
  // proper map + polygon draw tool. Schema, profile builder, and segment
  // resolver still support them for when the map picker lands.

  // ---------- Order history ----------
  { key: 'totalOrders',     path: 'totalOrders',     category: 'Order history', type: 'number', label: 'Total bookings',
    description: 'All bookings the customer has ever made, including canceled ones.',
    example: 'Total bookings is 0 — customers who signed up but never booked.' },
  { key: 'completedOrders', path: 'completedOrders', category: 'Order history', type: 'number', label: 'Completed bookings',
    description: 'Bookings that finished successfully. Excludes canceled and rejected ones.',
    example: 'Completed bookings is at least 3 — repeat customers with at least 3 successful washes.' },
  { key: 'canceledOrders',  path: 'canceledOrders',  category: 'Order history', type: 'number', label: 'Canceled bookings',
    description: 'Bookings that were canceled by the customer, service provider, or admin.',
    example: 'Canceled bookings is at least 2 — customers with a cancellation pattern, may need retention.' },
  { key: 'topService',      path: 'topService',      category: 'Order history', type: 'string', label: 'Service',  dataSource: 'service-by-id',
    description: 'The service the customer books most often.',
    example: 'Service is Standard wash — upsell these customers to Premium.',
    tips: ['Empty until the customer completes their first booking.'] },
  { key: 'topProvider',     path: 'topProvider',     category: 'Order history', type: 'string', label: 'Provider', dataSource: 'provider-by-id',
    description: 'The service provider the customer books with most often.',
    example: 'Provider is Dolphin Cars Wash — loyal customers of that provider.',
    tips: ['Empty until the customer completes their first booking.'] },

  // ---------- Recency ----------
  { key: 'registeredDaysAgo', path: 'registeredDaysAgo', category: 'Recency', type: 'number', label: 'Days since signup', unit: 'days',
    description: 'How many full days ago the customer created their account. 0 means today.',
    example: 'Days since signup is at most 7 — customers who signed up in the last week.' },
  { key: 'lastOrderDaysAgo',  path: 'lastOrderDaysAgo',  category: 'Recency', type: 'number', label: 'Days since last booking',   unit: 'days',
    description: 'How many full days since the customer\'s most recent booking. Empty for customers who never booked.',
    example: 'Days since last booking is more than 30 — customers who went quiet in the past month.',
    tips: [
      'Use with "is empty" to reach customers who never booked.',
      'Combine with "is more than 90" for classic churn win-backs.',
    ] },
  { key: 'registeredAt',      path: 'registeredAt',      category: 'Recency', type: 'date',   label: 'Signup date',
    description: 'The exact date the customer created their account.',
    example: 'Signup date between 1 Jan and 1 Feb — everyone who joined in January.' },
  { key: 'firstOrderAt',      path: 'firstOrderAt',      category: 'Recency', type: 'date',   label: 'First booking date',
    description: 'When the customer placed their very first booking. Empty if they never booked.',
    example: 'First booking date is empty — signed up but never booked.' },
  { key: 'lastOrderAt',       path: 'lastOrderAt',       category: 'Recency', type: 'date',   label: 'Latest booking date',
    description: 'When the customer placed their most recent booking.',
    example: 'Latest booking date before 1 May — customers whose last booking was before May.' },
  { key: 'checkoutStartedAt', path: 'checkoutStartedAt', category: 'Recency', type: 'date',   label: 'Currently in checkout',
    description: 'Set when a customer opens checkout. Clears the moment they book or leave.',
    example: 'Currently in checkout is set — customers with an open cart right now.',
    tips: ['Use "is set" to find open carts, "is empty" for the opposite.'] },

  // ---------- Monetary ----------
  { key: 'lifetimeValue',        path: 'lifetimeValue',        category: 'Monetary', type: 'number', label: 'Total spent (all time)',         unit: 'AED',
    description: 'How much the customer has paid across all completed bookings.',
    example: 'Total spent is at least 1000 AED — high-value customers.' },
  { key: 'last30dValue',         path: 'last30dValue',         category: 'Monetary', type: 'number', label: 'Spent (last 30 days)',  unit: 'AED',
    description: 'How much the customer has paid on completed bookings in the last 30 days.',
    example: 'Spent last 30 days is more than 500 — customers who are actively spending big right now.' },
  { key: 'last90dValue',         path: 'last90dValue',         category: 'Monetary', type: 'number', label: 'Spent (last 90 days)',  unit: 'AED',
    description: 'How much the customer has paid on completed bookings in the last 90 days.',
    example: 'Spent last 90 days is more than 1000 — sustained heavy spenders.' },
  { key: 'walletBalance',        path: 'walletBalance',        category: 'Monetary', type: 'number', label: 'Wallet balance',         unit: 'AED',
    description: 'Unused credit, cashback, and vouchers currently sitting in the customer\'s wallet.',
    example: 'Wallet balance is more than 20 — customers with enough credit to book right away.' },
  { key: 'expiringWalletInDays', path: 'expiringWalletInDays', category: 'Monetary', type: 'number', label: 'Wallet credit expiring in days', unit: 'days',
    description: 'Days until the customer\'s wallet credit expires. Empty if nothing is expiring soon.',
    example: 'Expiring in days is at most 7 — credit expires this week, urgency-driven nudge.' },

  // ---------- Lifecycle ----------
  { key: 'lifecycleStage',         path: 'lifecycleStage',         category: 'Lifecycle', type: 'enum',    label: 'Customer stage', enumValues: ['new', 'active', 'dormant', 'churned', 'highValue', 'vip'],
    description: 'Where the customer is in their journey with us, updated every night.',
    example: 'Customer stage is one of dormant or churned — classic win-back audience.',
    tips: [
      'new = signed up in the last 14 days with no bookings yet.',
      'active = booked in the last 30 days.',
      'dormant = last booking 30 to 90 days ago.',
      'churned = last booking more than 90 days ago.',
      'highValue = total spent is 1000 AED or more.',
      'vip = top 5% by total spent. VIP overrides all other stages.',
    ] },
  { key: 'vipCohort',              path: 'vipCohort',              category: 'Lifecycle', type: 'boolean', label: 'Is VIP customer',
    description: 'True for the top 5% of customers by total spending. Refreshed every night.',
    example: 'Is VIP customer is true — your highest-spending customers.',
    tips: ['Same customers as "Customer stage is vip", just as a simple yes/no.'] },
  // 'tags' hidden from the segment editor until the writer path lands. Schema,
  // profile builder, and segment resolver still support it — see MARKETING_CAMPAIGNS_PLAN.md §3.1.
  { key: 'hasActiveSubscription',  path: 'hasActiveSubscription',  category: 'Lifecycle', type: 'boolean', label: 'Has an active subscription',
    description: 'True if the customer has a paid subscription with washes still scheduled.',
    example: 'Has active subscription is false — exclude current subscribers from a discount pitch.' },
  { key: 'subscriptionExpiresAt',  path: 'subscriptionExpiresAt',  category: 'Lifecycle', type: 'date',    label: 'Subscription end date',
    description: 'The date of the customer\'s last scheduled wash under any subscription.',
    example: 'Subscription end date before today — customers whose subscription already ended, ready for a win-back.',
    tips: ['Empty for customers who never subscribed. Use "is empty" to reach them.'] },

  // ---------- Vehicle ----------
  { key: 'vehicleCount',  path: 'vehicleCount',  category: 'Vehicle', type: 'number', label: 'Number of vehicles',
    description: 'How many vehicles the customer has saved on their profile.',
    example: 'Number of vehicles is at least 2 — multi-car households, usually book more often.' },
  { key: 'vehicleTypes',  path: 'vehicleTypes',  category: 'Vehicle', type: 'array',  label: 'Vehicle types (Sedan, SUV…)',
    description: 'The categories of vehicles the customer owns (Sedan, SUV, motorbike, off-road…).',
    example: 'Vehicle types includes SUV — SUV owners, good target for a large-vehicle wash promo.' },

  // ---------- Reachability (server-enforced — these always AND with the segment) ----------
  // Marketing CAN'T uncheck these; resolver enforces them silently. Listed here so the
  // editor can show them as "always-on" badges, not as editable rules.
  { key: 'marketingOptIn', path: 'marketingOptIn', category: 'Reachability', type: 'boolean', label: 'Marketing opt-in', readOnly: true },
  { key: 'fcmToken',       path: 'fcmToken',       category: 'Reachability', type: 'string',  label: 'Has push token',   readOnly: true },
];

// --- Public helpers ---

// Available operators for a given field key — UI calls this to populate the operator dropdown.
export const operatorsForField = (fieldKey) => {
  const field = FIELD_CATALOG.find(f => f.key === fieldKey);
  if (!field) return [];
  return (OPS_BY_TYPE[field.type] || []).map(op => ({ value: op, ...OPERATORS[op] }));
};

// Look up a field's metadata by key.
export const getField = (fieldKey) => FIELD_CATALOG.find(f => f.key === fieldKey) || null;

// Fields grouped by category — UI iterates this to build the field picker dropdown.
export const fieldsByCategory = () => {
  const groups = {};
  for (const f of FIELD_CATALOG) {
    if (f.readOnly) continue; // don't expose reachability fields in the picker
    if (!groups[f.category]) groups[f.category] = [];
    groups[f.category].push(f);
  }
  return groups;
};

// Validate that a single rule { field, op, value } references a real field + valid operator.
// Returns null if valid, or a string error message.
export const validateRule = (rule) => {
  if (!rule || !rule.field) return 'field is required';
  const field = getField(rule.field);
  if (!field) return `unknown field: ${rule.field}`;
  if (field.readOnly) return `field is read-only: ${rule.field}`;
  if (!rule.op) return 'operator is required';
  const allowedOps = OPS_BY_TYPE[field.type] || [];
  if (!allowedOps.includes(rule.op)) return `operator ${rule.op} not allowed on type ${field.type}`;
  const arity = OPERATORS[rule.op].valueArity;
  if (arity === 'one' && (rule.value === undefined || rule.value === null || rule.value === '')) return 'value is required';
  if (arity === 'two' && (!Array.isArray(rule.value) || rule.value.length !== 2)) return 'between requires two values';
  if (arity === 'list' && (!Array.isArray(rule.value) || rule.value.length === 0)) return 'list operator requires at least one value';
  if ((field.type === 'geo' || field.type === 'geoList') && rule.op === 'withinRadius') {
    const v = rule.value;
    if (!v || typeof v.lat !== 'number' || typeof v.lng !== 'number' || typeof v.km !== 'number') {
      return 'withinRadius requires { lat, lng, km }';
    }
  }
  return null;
};
